import {
  EventTypeConsts,
  InteractionTypeConsts,
} from '@packages/systems/ix2/shared-constants';

const {
  MOUSE_CLICK_INTERACTION,
  MOUSE_HOVER_INTERACTION,
  MOUSE_MOVE_INTERACTION,
  SCROLL_INTO_VIEW_INTERACTION,
  SCROLLING_IN_VIEW_INTERACTION,
  MOUSE_MOVE_IN_VIEWPORT_INTERACTION,
  PAGE_IS_SCROLLING_INTERACTION,
  PAGE_LOAD_INTERACTION,
  PAGE_SCROLLED_INTERACTION,
  NAVBAR_INTERACTION,
  DROPDOWN_INTERACTION,
  ECOMMERCE_CART_INTERACTION,
  TAB_INTERACTION,
  SLIDER_INTERACTION,
} = InteractionTypeConsts;

const {
  NAVBAR_OPEN,
  NAVBAR_CLOSE,
  TAB_ACTIVE,
  TAB_INACTIVE,
  SLIDER_ACTIVE,
  SLIDER_INACTIVE,
  DROPDOWN_OPEN,
  DROPDOWN_CLOSE,
  MOUSE_CLICK,
  MOUSE_SECOND_CLICK,
  MOUSE_DOWN,
  MOUSE_UP,
  MOUSE_OVER,
  MOUSE_OUT,
  MOUSE_MOVE,
  MOUSE_MOVE_IN_VIEWPORT,
  SCROLL_INTO_VIEW,
  SCROLL_OUT_OF_VIEW,
  SCROLLING_IN_VIEW,
  ECOMMERCE_CART_OPEN,
  ECOMMERCE_CART_CLOSE,
  PAGE_START,
  PAGE_FINISH,
  PAGE_SCROLL_UP,
  PAGE_SCROLL_DOWN,
  PAGE_SCROLL,
} = EventTypeConsts;

const defaultEventConsts = {
  id: null,
  label: '',
  description: '',
  isContinuous: false,
  isPage: false,
} as const;

/**
 * General UI  + Logic
 */

export const EventConsts = {
  [NAVBAR_OPEN]: {
    ...defaultEventConsts,
    id: NAVBAR_OPEN,
    label: 'Open',
    description: 'When the Navbar opens.',
    interactionTypeId: NAVBAR_INTERACTION,
  },

  [NAVBAR_CLOSE]: {
    ...defaultEventConsts,
    id: NAVBAR_CLOSE,
    label: 'Close',
    description: 'When the Navbar closes.',
    interactionTypeId: NAVBAR_INTERACTION,
  },

  [TAB_ACTIVE]: {
    ...defaultEventConsts,
    id: TAB_ACTIVE,
    label: 'Active',
    description: 'When the tab becomes active.',
    interactionTypeId: TAB_INTERACTION,
  },

  [TAB_INACTIVE]: {
    ...defaultEventConsts,
    id: TAB_INACTIVE,
    label: 'Inactive',
    description: 'When the tab becomes inactive.',
    interactionTypeId: TAB_INTERACTION,
  },

  [SLIDER_ACTIVE]: {
    ...defaultEventConsts,
    id: SLIDER_ACTIVE,
    label: 'Active',
    description: 'When the slider becomes active.',
    interactionTypeId: SLIDER_INTERACTION,
  },

  [SLIDER_INACTIVE]: {
    ...defaultEventConsts,
    id: SLIDER_INACTIVE,
    label: 'Inactive',
    description: 'When the slider becomes inactive.',
    interactionTypeId: SLIDER_INTERACTION,
  },

  [DROPDOWN_OPEN]: {
    ...defaultEventConsts,
    id: DROPDOWN_OPEN,
    label: 'Open',
    description: 'When the dropdown opens.',
    interactionTypeId: DROPDOWN_INTERACTION,
  },

  [DROPDOWN_CLOSE]: {
    ...defaultEventConsts,
    id: DROPDOWN_CLOSE,
    label: 'Close',
    description: 'When the dropdown closes.',
    interactionTypeId: DROPDOWN_INTERACTION,
  },

  [MOUSE_CLICK]: {
    ...defaultEventConsts,
    id: MOUSE_CLICK,
    label: 'Mouse Click (Tap)',
    description: 'When an element is clicked or tapped.',
    interactionTypeId: MOUSE_CLICK_INTERACTION,
  },

  [MOUSE_SECOND_CLICK]: {
    ...defaultEventConsts,
    id: MOUSE_SECOND_CLICK,
    label: 'Mouse 2nd Click (Tap)',
    description: 'When an element is clicked or tapped.',
    interactionTypeId: MOUSE_CLICK_INTERACTION,
  },

  [MOUSE_DOWN]: {
    ...defaultEventConsts,
    id: MOUSE_DOWN,
    label: 'Mouse Down (Touch Start)',
    description: 'When the mouse is pressed down over an element.',
  },

  [MOUSE_UP]: {
    ...defaultEventConsts,
    id: MOUSE_UP,
    label: 'Mouse Up (Touch End)',
    description: 'When the mouse is released over an element.',
  },

  [MOUSE_OVER]: {
    ...defaultEventConsts,
    id: MOUSE_OVER,
    label: 'Mouse Over',
    description: 'When the mouse hovers over an element.',
    interactionTypeId: MOUSE_HOVER_INTERACTION,
  },

  [MOUSE_OUT]: {
    ...defaultEventConsts,
    id: MOUSE_OUT,
    label: 'Mouse Out',
    description: 'When the mouse leaves a hovered element.',
    interactionTypeId: MOUSE_HOVER_INTERACTION,
  },

  [MOUSE_MOVE]: {
    ...defaultEventConsts,
    id: MOUSE_MOVE,
    label: 'Mouse Move',
    description: 'While the mouse moves over an element.',
    isContinuous: true,
    interactionTypeId: MOUSE_MOVE_INTERACTION,
  },

  [MOUSE_MOVE_IN_VIEWPORT]: {
    ...defaultEventConsts,
    id: MOUSE_MOVE_IN_VIEWPORT,
    label: 'Mouse Move in Viewport',
    description: 'While the mouse moves inside a browser window.',
    isContinuous: true,
    isPage: true,
    interactionTypeId: MOUSE_MOVE_IN_VIEWPORT_INTERACTION,
  },

  [SCROLL_INTO_VIEW]: {
    ...defaultEventConsts,
    id: SCROLL_INTO_VIEW,
    label: 'Scroll Into View',
    description: 'When an element scrolls into view.',
    interactionTypeId: SCROLL_INTO_VIEW_INTERACTION,
  },

  [SCROLL_OUT_OF_VIEW]: {
    ...defaultEventConsts,
    id: SCROLL_OUT_OF_VIEW,
    label: 'Scroll Out of View',
    description: 'When an element scrolls out of view.',
    interactionTypeId: SCROLL_INTO_VIEW_INTERACTION,
  },

  [SCROLLING_IN_VIEW]: {
    ...defaultEventConsts,
    id: SCROLLING_IN_VIEW,
    label: 'Scrolling in View',
    description: 'When an element is scrolled in the viewport.',
    isContinuous: true,
    interactionTypeId: SCROLLING_IN_VIEW_INTERACTION,
  },

  [ECOMMERCE_CART_OPEN]: {
    ...defaultEventConsts,
    id: ECOMMERCE_CART_OPEN,
    label: 'Open',
    description: 'When the cart opens.',
    interactionTypeId: ECOMMERCE_CART_INTERACTION,
  },

  [ECOMMERCE_CART_CLOSE]: {
    ...defaultEventConsts,
    id: ECOMMERCE_CART_CLOSE,
    label: 'Close',
    description: 'When the cart closes.',
    interactionTypeId: ECOMMERCE_CART_INTERACTION,
  },

  [PAGE_START]: {
    ...defaultEventConsts,
    id: PAGE_START,
    label: 'Page Starts Loading',
    description: 'When the page begins loading.',
    isPage: true,
    interactionTypeId: PAGE_LOAD_INTERACTION,
  },

  [PAGE_FINISH]: {
    ...defaultEventConsts,
    id: PAGE_FINISH,
    label: 'Page Finished Loading',
    description: 'When all page assets have loaded.',
    isPage: true,
    interactionTypeId: PAGE_LOAD_INTERACTION,
  },

  [PAGE_SCROLL_UP]: {
    ...defaultEventConsts,
    id: PAGE_SCROLL_UP,
    label: 'Page Scrolled Up',
    description: 'When the page scrolls up.',
    isPage: true,
    interactionTypeId: PAGE_SCROLLED_INTERACTION,
  },

  [PAGE_SCROLL_DOWN]: {
    ...defaultEventConsts,
    id: PAGE_SCROLL_DOWN,
    label: 'Page Scrolled Down',
    description: 'When the page scrolls down.',
    isPage: true,
    interactionTypeId: PAGE_SCROLLED_INTERACTION,
  },

  [PAGE_SCROLL]: {
    ...defaultEventConsts,
    id: PAGE_SCROLL,
    label: 'Page Scroll',
    description: 'While the page is scrolling.',
    isContinuous: true,
    isPage: true,
    interactionTypeId: PAGE_IS_SCROLLING_INTERACTION,
  },
} as const;
